/**
 * WeeklyBriefCard — Sprint 7 · Project Symphony
 *
 * Rendered on the Campaign Room / Command Center once a week. Compact,
 * deterministic, no analytics.
 */
import { useQuery } from "@tanstack/react-query";
import { Calendar } from "lucide-react";

import { Card } from "@/shared/ui/card";
import { getWeeklyBrief } from "../api";

export function WeeklyBriefCard(): JSX.Element | null {
  const q = useQuery({
    queryKey: ["autopilot-brief-weekly"],
    queryFn: getWeeklyBrief,
    refetchInterval: 600_000, // every 10 minutes
  });

  if (!q.data) return null;
  const brief = q.data;

  return (
    <Card className="p-5" data-testid="weekly-brief-card">
      <div className="flex items-center gap-2 mb-3">
        <Calendar className="h-4 w-4 text-indigo-500" aria-hidden />
        <span className="text-xs uppercase tracking-widest text-muted-foreground">
          Weekly Brief
        </span>
      </div>
      <p className="text-sm font-semibold mb-3">{brief.headline}</p>
      <ul className="divide-y divide-border">
        {brief.sections.map((s) => (
          <li key={s.label} className="py-2 flex items-baseline gap-3">
            <span className="text-xs uppercase tracking-widest text-muted-foreground w-28 shrink-0">
              {s.label}
            </span>
            <span className="text-sm">{s.value}</span>
          </li>
        ))}
      </ul>
      {brief.recommendations.length > 0 && (
        <div className="mt-3 pt-3 border-t border-border">
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
            Recommendations
          </p>
          <ul className="space-y-1">
            {brief.recommendations.map((r, i) => (
              <li key={i} className="text-sm text-muted-foreground">
                — {r}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}
